import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  COLLECTION_HANDLES,
  fetchCollectionByHandle,
  type ShopCollection,
  type ShopProduct,
} from "@/lib/shopify";
import { useCart } from "@/lib/cart";

export const Route = createFileRoute("/merch/")({
  head: () => ({
    meta: [
      { title: "Merch — Real Pranks" },
      { name: "description", content: "Real Pranks official merchandise." },
      { property: "og:title", content: "Merch — Real Pranks" },
      { property: "og:description", content: "Real Pranks official merchandise." },
    ],
  }),
  component: MerchPage,
});

function formatMoney(amount: string, currencyCode: string) {
  const n = Number(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function ProductCard({ product }: { product: ShopProduct }) {
  const { addItem } = useCart();
  const [variantId, setVariantId] = useState<string>(product.variants[0]?.id ?? "");
  const [adding, setAdding] = useState(false);

  const variant = product.variants.find((v) => v.id === variantId) ?? product.variants[0];
  const image = product.images[0];
  const showVariantPicker =
    product.variants.length > 1 ||
    (product.variants[0]?.title && product.variants[0].title !== "Default Title");

  const handleAdd = async () => {
    if (!variant) return;
    setAdding(true);
    try {
      await addItem(variant.id, 1);
    } catch (e) {
      console.error("[merch] add failed", e);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-foreground/30">
      <Link
        to="/merch/$productHandle"
        params={{ productHandle: product.handle }}
        className="block aspect-square w-full overflow-hidden bg-secondary"
        aria-label={`View ${product.title}`}
      >
        {image ? (
          <img
            src={image.src}
            alt={image.altText ?? product.title}
            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full" />
        )}
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex-1">
          <Link
            to="/merch/$productHandle"
            params={{ productHandle: product.handle }}
            className="line-clamp-2 text-sm font-semibold transition-colors hover:text-muted-foreground"
          >
            {product.title}
          </Link>
          {variant?.price && (
            <p className="mt-1 text-sm text-muted-foreground">
              {formatMoney(variant.price.amount, variant.price.currencyCode)}
            </p>
          )}
        </div>

        {showVariantPicker && product.variants.length > 1 && (
          <select
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
            aria-label="Select variant"
          >
            {product.variants.map((v) => (
              <option key={v.id} value={v.id} disabled={!v.availableForSale}>
                {v.title}
                {!v.availableForSale ? " — Sold out" : ""}
              </option>
            ))}
          </select>
        )}

        <Button
          type="button"
          onClick={handleAdd}
          disabled={!variant || !variant.availableForSale || adding}
          className="w-full"
          size="sm"
        >
          {adding ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Adding…
            </>
          ) : !variant?.availableForSale ? (
            "Sold out"
          ) : (
            "Add to Cart"
          )}
        </Button>
      </div>
    </div>
  );
}

function MerchPage() {
  const [collections, setCollections] = useState<ShopCollection[]>([]);
  const [activeHandle, setActiveHandle] = useState<string>(COLLECTION_HANDLES[0].handle);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const results = await Promise.all(
          COLLECTION_HANDLES.map(async (c) => {
            const fetched = await fetchCollectionByHandle(c.handle);
            return fetched
              ? { ...fetched, title: c.title || fetched.title }
              : null;
          }),
        );
        if (!cancelled) {
          setCollections(results.filter((c): c is ShopCollection => c !== null));
        }
      } catch (e) {
        console.error("[merch] load failed", e);
        if (!cancelled) setError("We couldn't load the store right now. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCollection = collections.find((c) => c.handle === activeHandle);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-12">
        <header className="mb-8">
          <h1 className="text-3xl font-extrabold sm:text-4xl">Merch</h1>
          <p className="mt-2 text-muted-foreground">
            Official Real Pranks gear. Shipped worldwide.
          </p>
        </header>

        {!loading && !error && collections.length > 1 && (
          <div className="mb-8 flex flex-wrap gap-2 border-b border-border">
            {collections.map((c) => {
              const isActive = c.handle === activeHandle;
              return (
                <button
                  key={c.handle}
                  type="button"
                  onClick={() => setActiveHandle(c.handle)}
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? "border-foreground text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c.title}
                </button>
              );
            })}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            {error}
          </div>
        )}

        {!loading && !error && activeCollection && (
          activeCollection.products.length === 0 ? (
            <p className="text-sm text-muted-foreground">No products in this collection yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {activeCollection.products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}
